import React, { useEffect, useState } from "react";
import "./Addresses.scss";
import { Col, Space, Row, Select, Input, Button, Checkbox } from "antd";
import ProfileMap from "@components/ProfileMap/ProfileMap";
import Helpers from "@utils/Helpers";
import Suggestion from "@components/Suggestion/Suggestion";
import { IUserAddress } from "@utils/Interfaces";
import { PlusOutlined } from "@ant-design/icons";
import marker from "@assets/icons/location-marker (black).svg";
import { useUser } from "contexts/UserContext";
import { useAddress } from "./AddressHook";

interface IAddresses {
  setIsUpdating: (is: boolean) => void;
  isUpdating: boolean;
}

let debounceTimer: any;

const Addresses: React.FC<IAddresses> = ({ setIsUpdating }) => {
  const { userProfile, userAddresses, updateUserAddress, updateUserProfile } =
    useUser();
  const { updateAddress, addAddress, deleteAddress } = useAddress();
  const [selectedLocation, setSelectedLocation] = useState<any>({});
  const [isOpenSuggestion, setIsOpenSuggestion] = useState(false);
  const [searchResult, setSearchResult] = useState<any>([]);
  const [selectedPostion, setSelectedPostion] = useState<any[]>();
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [allAddress, setAllAddress] = useState<any>(null);
  const [selecttedAddressId, setSelecttedAddressId] = useState<any>(null);
  const [address, setAddress] = useState<IUserAddress>({
    ...allAddress?.[0],
  });

  useEffect(() => {
    if (!allAddress) {
      const getPrimary = userAddresses.find((item: any) => item.is_primary);
      let clonedAllAddress: any = [];
      if (getPrimary) {
        clonedAllAddress = [
          getPrimary,
          ...userAddresses.filter((item: any) => !item.is_primary),
        ];
      } else {
        const getUserAddress = userAddresses.find(
          (item: any) => item.address_id === userProfile?.address_id
        );
        clonedAllAddress = [
          getUserAddress,
          ...userAddresses.filter(
            (item: any) => item.address_id !== userProfile?.address_id
          ),
        ];
      }
      setAllAddress(clonedAllAddress);
      setSelectedPostion(
        clonedAllAddress.map((item: any) => {
          return [item?.lat || 0, item?.lon || 0];
        })
      );
      setSelecttedAddressId(clonedAllAddress[0]?.address_id);
    }
  }, []);

  useEffect(() => {
    if (selecttedAddressId && allAddress) {
      const selected = allAddress.find(
        (item: any) => item?.address_id === selecttedAddressId
      );
      if (selected) {
        const newSelectedPosition = Helpers.moveValueToFirst(selectedPostion, [
          selected.lat,
          selected.lon,
        ]);
        setSelectedPostion(newSelectedPosition);
        setAddress(selected);
      }
    }
  }, [selecttedAddressId]);

  const handleSelectedLocation = async (item: any) => {
    const zipCode = await Helpers.fetchZipCode(item.lat, item.lon);
    setIsOpenSuggestion(false);
    setSelectedLocation({ ...item, postCode: zipCode });
    const lat = parseFloat(item.lat);
    const long = parseFloat(item.lon);
    const newSelectedPosition = Helpers.moveValueToFirst(selectedPostion, [
      lat,
      long,
    ]);
    setSelectedPostion(newSelectedPosition);
    setIsEditing(true);
  };

  useEffect(() => {
    if (Object.keys(selectedLocation).length > 0) {
      setAddress((prev) => ({
        ...prev,
        street: selectedLocation.address.road
          ? selectedLocation.address.road
          : selectedLocation.display_name,
        city: selectedLocation.address.city
          ? selectedLocation.address.city
          : selectedLocation.address.town
          ? selectedLocation.address.town
          : selectedLocation.address.county
          ? selectedLocation.address.county
          : selectedLocation.address.village,
        province: selectedLocation.address.province
          ? selectedLocation.address.province
          : selectedLocation.address.state
          ? selectedLocation.address.state
          : selectedLocation.address.city,
        postal_code: selectedLocation.postCode,
        apartment: "",
        lat: parseFloat(selectedLocation.lat),
        lon: parseFloat(selectedLocation.lon),
        is_default: false,
      }));
      setIsEditing(true);
    }
  }, [selectedLocation]);

  const handleUpdateAddress = async () => {
    if (
      address.label === "" ||
      address.street === "" ||
      address.city === "" ||
      address.province === "" ||
      address.postal_code === ""
    ) {
      Helpers.notification.warning("Please fill in the require fields");
      return;
    }
    setIsUpdating(true);

    updateAddress(address, (response: any) => {
      setAllAddress(response.data.data.addresses);
      updateUserAddress(response.data.data.addresses);
      updateUserProfile(
        response.data.data.access_token,
        response.data.data.refresh_token
      );
      setSelecttedAddressId(response.data.data.addresses[0].address_id);
      setIsEditing(false);
      setIsAdding(false);
      setIsUpdating(false);
      Helpers.showSuccessUpdated("Update address successfully");
    });
  };

  const handleAddAddress = async () => {
    if (
      address.label === "" ||
      address.street === "" ||
      address.city === "" ||
      address.province === "" ||
      address.postal_code === ""
    ) {
      Helpers.notification.warning("Please fill in the require fields");
      return;
    }
    setIsUpdating(true);
    addAddress(address, (response: any) => {
      setAllAddress(response.data.data.addresses);
      updateUserAddress(response.data.data.addresses);
      updateUserProfile(
        response.data.data.access_token,
        response.data.data.refresh_token
      );
      setSelecttedAddressId(response.data.data.addresses[0].address_id);
      setIsAdding(false);
      setIsEditing(false);
      setIsUpdating(false);
      Helpers.showSuccessUpdated("Address added successfully");
    });
  };

  const handleDeleteAddress = async (address: any) => {
    if (address?.address_id === userProfile?.address_id) {
      Helpers.notification.warning("Cannot delete home address");
      return;
    }

    if (address?.address_id === userProfile?.farm_info.address_id) {
      Helpers.notification.warning("Cannot delete farm address");
      return;
    }

    setIsUpdating(true);
    deleteAddress(address, (response: any) => {
      setAllAddress(response.data.data.addresses);
      updateUserAddress(response.data.data.addresses);
      setSelecttedAddressId(allAddress[0].address_id);
      setIsEditing(false);
      setIsUpdating(false);
      Helpers.showSuccessUpdated("Address deleted successfully");
    });
  };

  const handleCancel = (address: any) => {
    setAddress(address);
    setSelectedPostion(allAddress.map((item: any) => [item.lat, item.lon]));
    setIsEditing(false);
  };

  return (
    <div className="Addresses">
      <Row gutter={[12, 12]} className="address-container">
        <Col span={12}>
          <ProfileMap
            type="Address"
            position={selectedPostion}
            setPosition={setSelectedPostion}
            onSelected={setSelectedLocation}
            isAdding={isAdding}
          />
        </Col>
        <Col span={12}>
          <div className="form-group">
            <Space className="space" direction="vertical">
              <div className="select-address">
                <Select
                  style={{ width: "100%" }}
                  value={selecttedAddressId}
                  onChange={(value) => setSelecttedAddressId(value)}
                >
                  {allAddress?.map((item: any, index: number) => (
                    <Select.Option key={index} value={item?.address_id}>
                      <span className="address-title">
                        <span
                          className="location-marker"
                          data-index={index + 1}
                        >
                          <img src={marker} alt="" />
                        </span>
                        {item?.label ? item?.label : `Address ${index + 1}`}
                      </span>
                      <span
                        className="postal-code"
                        style={{ float: "right", color: "#9a9a9a" }}
                      >
                        {item?.postal_code}
                      </span>
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  className="new-address"
                  onClick={() => {
                    setAddress({
                      label: "New Address",
                      street: "",
                      apartment: "",
                      city: "",
                      province: "",
                      postal_code: "",
                      lat: 0,
                      lon: 0,
                    });
                    setIsEditing(true);
                    setSelecttedAddressId("New Address");
                    setIsAdding(true);
                  }}
                >
                  {" "}
                  <PlusOutlined /> Address
                </Button>
              </div>
              <div className="form-item">
                <span className="label">
                  Address Name <span className="text-danger">*</span>
                </span>
                <Input
                  value={address?.label}
                  variant="filled"
                  size="large"
                  placeholder="Enter Address Name"
                  maxLength={22}
                  showCount
                  onChange={(e) => {
                    setAddress({ ...address, label: e.target.value });
                    setIsEditing(true);
                  }}
                />
              </div>
              <div className="form-item">
                <span className="label">
                  Street Address <span className="text-danger">*</span>
                </span>
                <Input
                  value={address?.street}
                  variant="filled"
                  size="large"
                  placeholder="Enter Street Address"
                  onChange={(e) => {
                    const value = e.target.value;
                    setIsOpenSuggestion(true);
                    if (value === "") {
                      setIsOpenSuggestion(false);
                    }
                    clearTimeout(debounceTimer);
                    debounceTimer = setTimeout(async () => {
                      const result = await Helpers.handleSearchLocation(value);
                      setSearchResult(result);
                    }, 300);
                    setAddress({ ...address, street: value });
                    setIsEditing(true);
                  }}
                />
              </div>
              {isOpenSuggestion && (
                <Suggestion
                  searchResult={searchResult}
                  onSelect={(selectedAddress: any) =>
                    handleSelectedLocation(selectedAddress)
                  }
                />
              )}
              <div className="form-item">
                <span className="label">
                  City <span className="text-danger">*</span>
                </span>
                <Input
                  value={address?.city || ""}
                  variant="filled"
                  size="large"
                  placeholder="Enter City"
                  onChange={(e) => {
                    setAddress({ ...address, city: e.target.value });
                    setIsEditing(true);
                  }}
                />
              </div>
              <div className="form-item">
                <span className="label">Apt, Suite, etc</span>
                <Input
                  value={address?.apartment || ""}
                  variant="filled"
                  size="large"
                  placeholder="Enter Apartment/Suite"
                  onChange={(e) => {
                    setAddress({ ...address, apartment: e.target.value });
                    setIsEditing(true);
                  }}
                />
              </div>
              <div className="form-item">
                <span className="label">
                  Province <span className="text-danger">*</span>
                </span>
                <Input
                  value={address?.province || ""}
                  variant="filled"
                  size="large"
                  placeholder="Enter Province"
                  onChange={(e) => {
                    setAddress({ ...address, province: e.target.value });
                    setIsEditing(true);
                  }}
                />
              </div>
              <div className="form-item">
                <span className="label">
                  Postal/ZIP Code <span className="text-danger">*</span>
                </span>
                <Input
                  value={address?.postal_code || ""}
                  variant="filled"
                  size="large"
                  placeholder="Enter Postal/ZIP Code"
                  onChange={(e) => {
                    setAddress({ ...address, postal_code: e.target.value });
                    setIsEditing(true);
                  }}
                />
              </div>
              <Checkbox
                checked={address?.is_primary}
                onChange={() => {
                  setAddress({ ...address, is_primary: !address.is_primary });
                  setIsEditing(true);
                }}
              >
                Set as default address
              </Checkbox>
            </Space>
          </div>
        </Col>
        <div className="form-footer">
          <Button
            className="delete-btn"
            onClick={() => handleDeleteAddress(address)}
          >
            Delete
          </Button>
          <div className="confirm-btn">
            <Button
              className="cancel-btn"
              onClick={() =>
                handleCancel(
                  allAddress.find(
                    (item: any) => item.address_id === selecttedAddressId
                  )
                )
              }
            >
              Cancel
            </Button>
            {isAdding ? (
              <Button
                className="save-btn"
                onClick={handleAddAddress}
                disabled={!isEditing}
              >
                Add
              </Button>
            ) : (
              <Button
                className="save-btn"
                onClick={handleUpdateAddress}
                disabled={!isEditing}
              >
                Save
              </Button>
            )}
          </div>
        </div>
      </Row>
    </div>
  );
};

export default Addresses;
